import { ShieldCheck, Award, Users, CheckCircle, FileCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { companyInfo } from '../mockData';

export const About = () => {
  const values = [
    {
      icon: ShieldCheck,
      title: "Safety First",
      description: "Every job we undertake prioritizes the safety of your property and its occupants."
    },
    {
      icon: Award,
      title: "Quality Workmanship",
      description: "NICEIC approved contractors delivering work to the highest standards."
    },
    {
      icon: Users,
      title: "Customer Focused",
      description: "Building long-term relationships based on trust, reliability, and excellent service."
    }
  ];

  const commitments = [
    "All work carried out by qualified, experienced electricians",
    "NICEIC approved contractor status",
    "Fully insured for your peace of mind",
    "Transparent pricing with no hidden costs",
    "Clean, professional service on every job",
    "Fast turnaround times without compromising quality",
    "Comprehensive certifications for all work",
    "Ongoing support and maintenance available"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-700 text-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">About Us</h1>
          <p className="text-xl text-blue-100 max-w-2xl">
            Your trusted electrical partner across Greater London
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_landlord-safety-1/artifacts/ez010v5h_crescent.JPG" 
              alt="Crescent Electrical Ltd"
              className="h-32 w-auto mx-auto mb-6"
            />
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Professional Electrical Services You Can Trust
              </h2>
              <div className="space-y-4 text-lg text-gray-600">
                <p>
                  {companyInfo.name} is an NICEIC Approved Contractor serving Greater London with 
                  comprehensive electrical installation and compliance services.
                </p>
                <p>
                  We specialize in working with landlords, letting agents, and property managers, 
                  providing fast, reliable, and fully certified electrical services. Our focus is on 
                  safety, compliance, and building long-term partnerships with our clients.
                </p>
                <p>
                  From EICR inspections and consumer unit upgrades to full rewires and emergency lighting 
                  certification, we deliver professional solutions tailored to your property's needs.
                </p>
                <p>
                  Our reputation is built on trust, quality workmanship, and exceptional customer service. 
                  With a 5.0 Google rating and NICEIC approval, you can be confident you're working with 
                  true professionals.
                </p>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1660330589693-99889d60181e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBlbGVjdHJpY2lhbiUyMHdvcmtpbmd8ZW58MHx8fHwxNzcxNjA1MTE2fDA&ixlib=rb-4.1.0&q=85"
                alt="Professional electrician"
                className="rounded-2xl shadow-xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <p className="text-sm font-semibold text-gray-700 mb-1">NICEIC Approved</p>
                <p className="text-2xl font-bold text-blue-600">Contractor</p>
              </div>
            </div>
          </div>

          {/* Our Values */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Values</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <Card key={index} className="text-center border-gray-200">
                    <CardHeader>
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon className="h-8 w-8 text-blue-600" />
                      </div>
                      <CardTitle className="text-xl">{value.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600">{value.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Our Commitment */}
          <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Accreditations</h2>
            <div className="flex flex-wrap justify-center items-center gap-12 mb-8">
              <div className="text-center">
                <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-4 inline-block">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_landlord-safety-1/artifacts/s7t87tk8_ukas2.jpg"
                    alt="NICEIC Approved Contractor & UKAS Product Certification"
                    className="h-32 w-auto"
                  />
                </div>
                <p className="font-bold text-gray-900">NICEIC Approved Contractor</p>
                <p className="text-sm text-gray-600">& UKAS Product Certification 7975</p>
              </div>
              <div className="text-center">
                <div className="w-32 h-32 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileCheck className="h-16 w-16 text-green-600" />
                </div>
                <p className="font-bold text-gray-900">PRS</p>
                <p className="text-sm text-gray-600">Private Rented Sector</p>
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Commitment to You</h2>
            <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {commitments.map((commitment, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{commitment}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-4xl font-bold text-blue-600 mb-2">5.0★</p>
              <p className="text-gray-600">Google Rating</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-blue-600 mb-2">100+</p>
              <p className="text-gray-600">Happy Clients & Counting</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-blue-600 mb-2">24-48hr</p>
              <p className="text-gray-600">Avg Turnaround</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-blue-600 mb-2">100%</p>
              <p className="text-gray-600">NICEIC Certified</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
